import React from "react";

export interface ErrorStateProps {
  title?: string;
  message?: string;
  error?: Error | { message: string } | null;
  onRetry?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

/**
 * Reusable error state component
 * Provides consistent error UI across the application
 * Follows accessibility best practices
 */
export function ErrorState({
  title = "Something went wrong",
  message,
  error,
  onRetry,
  icon,
  className = "",
}: ErrorStateProps) {
  const errorMessage =
    message || error?.message || "An unexpected error occurred";

  return (
    <div
      className={`flex h-full min-h-[200px] items-center justify-center ${className}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="text-center">
        {icon || (
          <i
            className="ri-error-warning-line mb-2 text-4xl text-red-500"
            aria-hidden="true"
          />
        )}
        <h3 className="mb-2 text-lg font-semibold text-neutral-950">{title}</h3>
        <p className="mb-4 text-sm text-neutral-600">{errorMessage}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="bg-primary hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
            aria-label="Retry the failed operation"
          >
            <i className="ri-refresh-line" aria-hidden="true" />
            Try Again
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Error state specifically for tables
 */
export function TableErrorState({
  message = "Failed to load data. Please try again.",
  onRetry,
}: Pick<ErrorStateProps, "message" | "onRetry">) {
  return (
    <div className="flex h-full min-h-0 w-full flex-col space-y-5 rounded-lg bg-white p-5">
      <ErrorState message={message} onRetry={onRetry} />
    </div>
  );
}

/**
 * Empty state component for when there's no data
 */
export function EmptyState({
  icon,
  title = "No data found",
  message = "There is no data to display at this time.",
  action,
  className = "",
  isDarkMode = false,
}: {
  icon?: React.ReactNode;
  title?: string;
  message?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  isDarkMode?: boolean;
}) {
  return (
    <div
      className={`flex h-full min-h-[200px] items-center justify-center ${className}`}
      role="status"
    >
      <div className="text-center">
        {icon || (
          <i
            className="ri-inbox-line mb-2 text-4xl text-neutral-400"
            aria-hidden="true"
          />
        )}
        <h3
          className={`mb-2 text-lg font-semibold ${isDarkMode ? "text-white" : "text-neutral-950"}`}
        >
          {title}
        </h3>
        <p
          className={`mb-4 text-sm ${isDarkMode ? "text-white/50" : "text-neutral-400"}`}
        >
          {message}
        </p>
        {action && (
          <button
            onClick={action.onClick}
            className="bg-primary hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}
